import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Loader2, UserCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TestLoginPage = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error('Please enter username and password');
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/test-login`, { username, password });
      setLoggedIn(true);
      setUserInfo(data);
      toast.success('Login successful!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-teal-200/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-cyan-200/20 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-teal-500/30">
            <ShoppingCart className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold font-['Outfit'] text-slate-800">SmartCaddy</h1>
          <p className="text-slate-500">Test Account Verification</p>
        </div>

        <Card className="border-slate-200 bg-white/80 backdrop-blur-xl shadow-xl shadow-slate-200/50">
          {!loggedIn ? (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-slate-800">
                  <UserCheck className="w-5 h-5 text-teal-600" />
                  Test Login
                </CardTitle>
                <CardDescription className="text-slate-500">
                  Enter your test account credentials
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="username" className="text-slate-700">Username</Label>
                    <Input
                      id="username"
                      type="text"
                      placeholder="test_user"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="h-11 bg-slate-50 border-slate-300"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-slate-700">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-11 bg-slate-50 border-slate-300"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-11 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold rounded-xl shadow-lg shadow-teal-500/25"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </form>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-600">
                  <UserCheck className="w-5 h-5" />
                  Login Successful
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <p className="text-sm text-emerald-800">
                    <strong>Username:</strong> {userInfo?.username}
                  </p>
                  <p className="text-sm text-emerald-600 mt-2">
                    This test account is working correctly. You can now use these credentials for Razorpay website verification.
                  </p>
                </div>
                
                <Button
                  variant="outline"
                  onClick={() => { setLoggedIn(false); setUserInfo(null); setUsername(''); setPassword(''); }}
                  className="w-full"
                >
                  Test Another Account
                </Button>
                
                <Button
                  onClick={() => navigate('/')}
                  className="w-full bg-teal-500 hover:bg-teal-600"
                >
                  Go to Store
                </Button>
              </CardContent>
            </>
          )}
        </Card>

        <p className="text-center text-sm text-slate-500 mt-6">
          <button 
            onClick={() => navigate('/')}
            className="hover:text-teal-600 transition-colors"
          >
            ← Back to Store
          </button>
        </p>
      </div>
    </div>
  );
};

export default TestLoginPage;
